import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { baseGroundY } from '../city/cityGround';

// roomSpace — WORLD SPACE IN, ROOM SPACE OUT. Extracted from InteriorResidentPanel
// (2026-10-29) so the story-mark tool converts the same way, not a second copy.
//
// The camera reports where it is in the WORLD. Things that live in a room
// (residents, story marks) are saved in ROOM coordinates, the space the room's
// own spawn is authored in, and MeshInterior renders that space inside a group
// that is both MOVED to the venue's street address and TURNED to face its street:
//
//     world = anchor + R(rotY) · local
//
// so recovering `local` means undoing both, in that order. Subtracting the
// anchor alone was half the transform, and with the Corner Market turned 270°
// the remaining rotation threw every placement a quarter turn out.
//
// `worldYaw` (0 = +z) comes back in the room's frame as `rotY`.

/**
 * WHOSE SITE A SITED ROOM STANDS ON (2026-11-01): its own — a venue the town
 * built — or, for a Hype-U classroom (city/hypeu), the Hype-U lobby's: one
 * building, a floor per course, raised by the room's `floorY` (world units).
 * Khari: "there are windows in the classroom, we should place the city view
 * outside, but from an elevated view to simulate being on a higher floor."
 * Everything that finds a sited room's anchor, turn or lift asks this first.
 */
export function siteKey(interior) {
  const room = ROOMS[interior];
  if (room?.hypeu !== 'class') return interior;
  const lobby = Object.keys(ROOMS).find((id) => ROOMS[id]?.hypeu === 'lobby' && ROOMS[id].registry && ROOMS[id].placement === 'sited');
  return lobby || interior;
}
/** Stands on the town's ground like a venue it built: its own site, or the one it shares. */
export const townSited = (room) => !!room && room.placement === 'sited' && (!!room.registry || room.hypeu === 'class');

/**
 * Which way a SITED room is turned in the city (radians), the one rule every
 * reader uses (MeshInterior draws it, PlanetScene lands you in it, this file
 * converts into it): the placement tool's draft, then the saved placement, then
 * — for a venue the town built (2026-10-31: The Jahva House, HypeTunes HQ seeing
 * the live street) — its SITE's own turn, so the room faces the street its
 * building faces; then rooms.js `modelRotY`. A virtual room is never turned.
 */
export function sitedTurn(interior, st = useWorldStore.getState()) {
  const room = ROOMS[interior];
  if (room?.placement !== 'sited') return 0;
  const key = siteKey(interior);
  const site = townSited(room) ? st.venueRegistry?.[key]?.site : null;
  return st.venuePlaceDraft?.[key]?.rotY ?? st.venueAnchorOverrides?.[key]?.rotY
    ?? (site && Number.isFinite(Number(site.rotY)) ? Number(site.rotY) : null) ?? room.modelRotY ?? 0;
}

/**
 * How high a SITED room the town built stands (2026-10-31): on the street's
 * own ground at its anchor (city/cityGround: the pavement is 0.086u up), so the
 * city's ground surface stays just under the room's floor instead of cutting
 * 19cm up through it. A hair more, so the two never fight. The built-in sited
 * rooms keep the height they were tuned at (their own nudges assume 0).
 */
export function sitedLift(interior, st = useWorldStore.getState()) {
  const room = ROOMS[interior];
  if (!townSited(room)) return 0;
  const a = st.venueAnchors?.[siteKey(interior)];
  return a ? baseGroundY(a[0], a[2]) + 0.004 + (Number(room.floorY) || 0) : 0;
}

export function toRoomLocal(interior, x, z, worldYaw = 0) {
  const st = useWorldStore.getState();
  const sited = ROOMS[interior]?.placement === 'sited';
  const anchor = sited ? st.venueAnchors?.[siteKey(interior)] : null;
  // The room's facing, resolved the same way MeshInterior resolves it (sitedTurn).
  const roomRot = sitedTurn(interior, st);
  if (anchor) { x -= anchor[0]; z -= anchor[2]; }
  if (roomRot) {
    const c = Math.cos(-roomRot); const s = Math.sin(-roomRot);
    const rx = x * c + z * s;
    const rz = -x * s + z * c;
    x = rx; z = rz;
  }
  return { x, z, rotY: worldYaw - roomRot, roomRot };
}
